/**
 * Extrae IDs de transacción presentes en el payload de crear/actualizar pedido.
 * Soporta cuerpo transformado (meta_data + pagos) y forma OrderData (extraDetails + pagos).
 *
 * En **plan separe** y **pagos parciales** puede haber más de un `id_transaccion` (varias filas);
 * se deduplican para el lookup pero se validan todos los distintos.
 * No se incluyen IDs en filas de efectivo, contra entrega ni MDM.
 */
import {
  normalizeOrderPaymentMethod,
  orderPaymentMethodIsPartOrPlanSepare,
  parcialRowContributesTransactionIdForLookup,
  singleOrderPaymentMethodUsesTransactionId
} from './orderPaymentTransactionRules'

function pushIfNonEmpty(set: Set<string>, value: unknown) {
  if (value === undefined || value === null) return
  const s = String(value).trim()
  if (s) set.add(s)
}

function orderPaymentMethodFromPayload(d: Record<string, unknown>): string {
  const fromRoot = normalizeOrderPaymentMethod(d.payment_method)
  if (fromRoot) return fromRoot
  const extra = d.extraDetails
  if (extra && typeof extra === 'object') {
    const fromEx = normalizeOrderPaymentMethod((extra as Record<string, unknown>).payment_method)
    if (fromEx) return fromEx
  }
  const meta = d.meta_data
  if (Array.isArray(meta)) {
    const pmEntry = meta.find((m: { key?: string; value?: unknown }) => m?.key === 'payment_method')
    if (pmEntry?.value !== undefined && pmEntry?.value !== null) {
      return normalizeOrderPaymentMethod(pmEntry.value)
    }
  }
  return ''
}

function collectFromPagoRows(set: Set<string>, rows: unknown[]) {
  for (const p of rows) {
    if (p && typeof p === 'object') {
      const row = p as Record<string, unknown>
      if (parcialRowContributesTransactionIdForLookup(row.metodo_pago)) {
        pushIfNonEmpty(set, row.id_transaccion)
      }
    }
  }
}

export function collectTransactionIdsFromOrderPayload(data: unknown): string[] {
  const set = new Set<string>()
  if (!data || typeof data !== 'object') return []

  const d = data as Record<string, unknown>
  const orderPm = orderPaymentMethodFromPayload(d)

  if (orderPaymentMethodIsPartOrPlanSepare(orderPm)) {
    const extra = d.extraDetails
    if (extra && typeof extra === 'object') {
      const pagosEx = (extra as Record<string, unknown>).pagos
      if (Array.isArray(pagosEx)) collectFromPagoRows(set, pagosEx)
    }
    const pagos = d.pagos
    if (Array.isArray(pagos)) collectFromPagoRows(set, pagos)
    return Array.from(set)
  }

  const usesSingle =
    singleOrderPaymentMethodUsesTransactionId(orderPm) || normalizeOrderPaymentMethod(orderPm) === 'bold'
  if (!usesSingle) {
    return []
  }

  pushIfNonEmpty(set, d.transaction_id)

  const extra = d.extraDetails
  if (extra && typeof extra === 'object') {
    const ex = extra as Record<string, unknown>
    pushIfNonEmpty(set, ex.transaction_id)
  }

  const meta = d.meta_data
  if (Array.isArray(meta)) {
    const entry = meta.find((m: { key?: string; value?: unknown }) => m?.key === 'transaction_id')
    if (entry?.value !== undefined && entry?.value !== null) {
      pushIfNonEmpty(set, entry.value)
    }
  }

  return Array.from(set)
}
